import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Node, mergeAttributes } from "@tiptap/core";
import { useEffect } from "react";
import {
  Bold, Italic, Strikethrough, List, ListOrdered,
  Link as LinkIcon, Image as ImageIcon, Undo, Redo, Quote,
  Table as TableIcon, Info, AlertTriangle, Lightbulb, AlertOctagon, ChevronDown,
} from "lucide-react";
import { cn } from "../../lib/utils";

// Callout node: renders <div class="callout callout-{variant}">, variant read
// from the class so it survives server-side sanitisation.
const Callout = Node.create({
  name: "callout",
  group: "block",
  content: "block+",
  defining: true,
  addAttributes() {
    return {
      variant: {
        default: "info",
        parseHTML: (el) => (el.className.match(/callout-(\w+)/) || [])[1] || "info",
        renderHTML: (attrs) => ({ class: `callout callout-${attrs.variant}` }),
      },
    };
  },
  parseHTML() { return [{ tag: "div.callout" }]; },
  renderHTML({ HTMLAttributes }) { return ["div", mergeAttributes(HTMLAttributes), 0]; },
  addCommands() {
    return {
      toggleCallout: (variant) => ({ commands }) => commands.toggleWrap(this.name, { variant }),
    };
  },
});

function Btn({ onClick, active, disabled, title, children, testid }) {
  return (
    <button type="button" data-testid={testid} title={title} disabled={disabled} onClick={onClick}
      className={cn("grid place-items-center h-8 w-8 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30",
        active && "bg-gold/20 text-gold")}>
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, minimal = false }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4, 5, 6] }, link: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image.configure({ HTMLAttributes: { loading: "lazy" } }),
      Table.configure({ resizable: false, HTMLAttributes: { class: "cf-table" } }),
      TableRow, TableHeader, TableCell,
      Callout,
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none min-h-[200px] px-4 py-3 focus:outline-none text-white/90",
        "data-testid": "rte-content",
      },
    },
  });

  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt("Link URL");
    if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    else editor.chain().focus().unsetLink().run();
  };
  const addImage = () => {
    const url = window.prompt("Image URL");
    const alt = url ? window.prompt("Image ALT text (for SEO & accessibility)") || "" : "";
    if (url) editor.chain().focus().setImage({ src: url, alt }).run();
  };

  const headingValue = [2, 3, 4, 5, 6].find((l) => editor.isActive("heading", { level: l })) || 0;
  const setHeading = (e) => {
    const lvl = Number(e.target.value);
    if (lvl === 0) editor.chain().focus().setParagraph().run();
    else editor.chain().focus().toggleHeading({ level: lvl }).run();
  };

  return (
    <div className="rounded-xl border border-white/15 bg-black/30 overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-white/10 px-2 py-1.5 bg-white/[0.03]">
        <div className="relative">
          <select value={headingValue} onChange={setHeading} data-testid="rte-heading-select"
            className="appearance-none bg-transparent text-white/70 text-sm rounded-md pl-2 pr-6 py-1 hover:bg-white/10 focus:outline-none cursor-pointer">
            <option value={0} className="bg-neutral-900">Paragraph</option>
            <option value={2} className="bg-neutral-900">Heading 2</option>
            <option value={3} className="bg-neutral-900">Heading 3</option>
            <option value={4} className="bg-neutral-900">Heading 4</option>
            <option value={5} className="bg-neutral-900">Heading 5</option>
            <option value={6} className="bg-neutral-900">Heading 6</option>
          </select>
          <ChevronDown className="h-3 w-3 absolute right-1.5 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
        </div>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <Btn testid="rte-bold" title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></Btn>
        <Btn testid="rte-italic" title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>
        <Btn testid="rte-strike" title="Strikethrough" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></Btn>
        <Btn testid="rte-bullet" title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></Btn>
        <Btn testid="rte-ordered" title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Btn>
        <Btn testid="rte-quote" title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></Btn>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <Btn testid="rte-link" title="Link" active={editor.isActive("link")} onClick={addLink}><LinkIcon className="h-4 w-4" /></Btn>
        <Btn testid="rte-image" title="Image" onClick={addImage}><ImageIcon className="h-4 w-4" /></Btn>
        {!minimal && (
          <>
            <Btn testid="rte-table" title="Insert table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><TableIcon className="h-4 w-4" /></Btn>
            <div className="w-px h-5 bg-white/10 mx-1" />
            <Btn testid="rte-callout-info" title="Info callout" active={editor.isActive("callout", { variant: "info" })} onClick={() => editor.chain().focus().toggleCallout("info").run()}><Info className="h-4 w-4 text-sky-400" /></Btn>
            <Btn testid="rte-callout-warning" title="Warning callout" active={editor.isActive("callout", { variant: "warning" })} onClick={() => editor.chain().focus().toggleCallout("warning").run()}><AlertTriangle className="h-4 w-4 text-amber-400" /></Btn>
            <Btn testid="rte-callout-tip" title="Tip callout" active={editor.isActive("callout", { variant: "tip" })} onClick={() => editor.chain().focus().toggleCallout("tip").run()}><Lightbulb className="h-4 w-4 text-green-400" /></Btn>
            <Btn testid="rte-callout-important" title="Important callout" active={editor.isActive("callout", { variant: "important" })} onClick={() => editor.chain().focus().toggleCallout("important").run()}><AlertOctagon className="h-4 w-4 text-red-400" /></Btn>
          </>
        )}
        <div className="w-px h-5 bg-white/10 mx-1" />
        <Btn testid="rte-undo" title="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo className="h-4 w-4" /></Btn>
        <Btn testid="rte-redo" title="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo className="h-4 w-4" /></Btn>
      </div>
      {editor.isActive("table") && (
        <div className="flex flex-wrap gap-1 px-2 py-1 border-b border-white/10 bg-white/[0.02] text-[11px]">
          <TblBtn onClick={() => editor.chain().focus().addColumnAfter().run()}>+ Col</TblBtn>
          <TblBtn onClick={() => editor.chain().focus().deleteColumn().run()}>− Col</TblBtn>
          <TblBtn onClick={() => editor.chain().focus().addRowAfter().run()}>+ Row</TblBtn>
          <TblBtn onClick={() => editor.chain().focus().deleteRow().run()}>− Row</TblBtn>
          <TblBtn onClick={() => editor.chain().focus().deleteTable().run()}>Delete table</TblBtn>
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}

function TblBtn({ onClick, children }) {
  return <button type="button" onClick={onClick} className="px-2 py-0.5 rounded bg-white/5 hover:bg-white/10 text-white/70">{children}</button>;
}
